<template>
  <div
    v-if="formStepId <= interactedSteps"
    v-show="currentStep == formStepId"
    :class="'step' + formStepId"
  >
    <div
      v-for="(component, index) in settings.componentSettings"
      :key="`${component.componentName}${formStepId}${index}`"
      :class="backgroundColour(component.backgroundColour)"
    >
      <div
        :class="
          blockContainer(component.isBlockContainer, component.isBlockCompact)
        "
      >
        <component
          :is="component.componentName"
          v-bind="component.componentProps"
          :validationScope="validationScope"
        ></component>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import VeeValidate, { Validator } from 'vee-validate';
import {
  Component, Prop, Vue, Watch, Provide
} from 'vue-property-decorator';
import {
  namespace
} from 'vuex-class';
import { scrollService } from '@/services/ScrollService';

// Audience Segmentation
import AudienceSegmentationComponent from '../audience-segmentation/AudienceSegmentationComponent.vue';

// Business
import BusinessInformationComponent from '../business/BusinessInformationComponent.vue';

// Consent
import PresubMarketingConsentComponent from '../consent/PresubMarketingConsentComponent.vue';
import NissanPresubMultiConsentComponent from '../consent/NissanPresubMultiConsentComponent.vue';
import MandatoryConsentComponent from '../consent/MandatoryConsentComponent.vue';
import PresubMultiConsentComponent from '../consent/PresubMultiConsentComponent.vue';
import StellantisConsentComponent from '../consent/StellantisConsentComponent.vue';
import MercedesPostEnquiryConsentComponent from '../consent/MercedesPostEnquiryConsentComponent.vue';

// Enquiry
import PersonalDetailsComponent from '../enquiry/PersonalDetailsComponent.vue';
import PhoneNumberComponent from '../enquiry/PhoneNumberComponent.vue';
import AddressLookupComponent from '../enquiry/AddressLookupComponent.vue';
import CustomerSatisfactionComponent from '../enquiry/CustomerSatisfactionComponent.vue';
import NPSComponent from '../enquiry/NPSComponent.vue';
import EnquiryChoicesComponent from '../enquiry/EnquiryChoicesComponent.vue';
import PreviousManufacturerDetailsComponent from '../enquiry/PreviousManufacturerDetailsComponent.vue';
import ContactPreferenceComponent from '../enquiry/ContactPreferenceComponent.vue';
import CustomerTypeComponent from '../enquiry/CustomerTypeComponent.vue';
import VisitedDealershipComponent from '../enquiry/VisitedDealershipComponent.vue';
import DigitalBrochureInterestComponent from '../enquiry/DigitalBrochureInterestComponent.vue';
import VerificationCodeComponent from '../enquiry/VerificationCodeComponent.vue';
import EnquiryServicesComponent from '../enquiry/EnquiryServicesComponent.vue';
import CurrentRecentManufacturerDetailsComponent from '../enquiry/CurrentRecentManufacturerDetailsComponent.vue';
import RetailerSelectionComponent from '../enquiry/RetailerSelectionComponent.vue';
import RetailerConfirmationComponent from '../enquiry/RetailerConfirmationComponent.vue';
import ExtraDataComponent from '../enquiry/ExtraDataComponent.vue';

// Finance
import PurchaseTimeComponent from '../finance/PurchaseTimeComponent.vue';
import PurchaseTypeComponent from '../finance/PurchaseTypeComponent.vue';
import VehicleChangeFrequencyComponent from '../finance/VehicleChangeFrequencyComponent.vue';
import PurchaseTypeGroupComponent from '../finance/PurchaseTypeGroupComponent.vue';
import FundingTypeComponent from '../finance/FundingTypeComponent.vue';
import LeaseInformationComponent from '../finance/LeaseInformationComponent.vue';
import FinanceInformationComponent from '../finance/FinanceInformationComponent.vue';

// General
import AccordionComponent from '../general/AccordionComponent.vue';
import ProgressBarComponent from '../general/ProgressBarComponent.vue';
import TestimonialsComponent from '../general/TestimonialsComponent.vue';
import CarouselComponent from '../general/CarouselComponent.vue';
import StepHeaderComponent from '../general/StepHeaderComponent.vue';
import StepHeaderListComponent from '../general/StepHeaderListComponent.vue';
import CompanyDetailsComponent from '../general/CompanyDetailsComponent.vue';
import USPComponent from '../general/USPComponent.vue';
import PublisherContentComponent from '../general/PublisherContentComponent.vue';
import YouTubeComponent from '../general/YouTubeComponent.vue';
import CountdownComponent from '../general/CountdownComponent.vue';
import HighlightsComponent from '../general/HighlightsComponent.vue';
import CollageComponent from '../general/CollageComponent.vue';
import BlockBodyComponent from '../general/BlockBodyComponent.vue';
import ReviewComponent from '../bmw/ReviewComponent.vue';
import CallMeNowComponent from '../general/CallMeNowComponent.vue';
import MotorwayComponent from '../general/MotorwayComponent.vue';
import MotorwayValuationWrapperComponent from '../general/MotorwayValuationWrapperComponent.vue';
import EmailCaptureComponent from '../general/EmailCaptureComponent.vue';
import ExitIntentSurveyComponent from '../general/ExitIntentSurveyComponent.vue';
import HelpBannerComponent from '../general/HelpBannerComponent.vue';

// KMI
import KMIPersonalDetailsComponent from '../kmi/KMIPersonalDetailsComponent.vue';
import RecommendedArticlesComponent from '../kmi/RecommendedArticlesComponent.vue';
import KMIInterestComponent from '../kmi/KMIInterestComponent.vue';

// Navigation
import NextButtonComponent from './NextButtonComponent.vue';
import ScrollToComponent from './ScrollToComponent.vue';
import StartAgainComponent from './StartAgainComponent.vue';
import SubmitButtonComponent from './SubmitButtonComponent.vue';
import GoToStepButtonComponent from './GoToStepButtonComponent.vue';

// Pricing
import BlurredPricingCurveBenefitsComponent from '../pricing/BlurredPricingCurveBenefitsComponent.vue';
import BlurredPricingCurveComponent from '../pricing/BlurredPricingCurveComponent.vue';
import DerivativePricingComponent from '../pricing/DerivativePricingComponent.vue';
import GenericPricingComponent from '../pricing/GenericPricingComponent.vue';
import PricingDataComponent from '../pricing/PricingDataComponent.vue';
import PriceHintComponent from '../pricing/PriceHintComponent.vue';

// Settings
import PostEnquirySettingsComponent from '../settings/PostEnquirySettingsComponent.vue';

// Steps
import VehicleSelectionStepComponent from '../steps/VehicleSelectionStepComponent.vue';
import ModelOptionsStepComponent from '../steps/ModelOptionsStepComponent.vue';
import AddressStepComponent from '../steps/AddressStepComponent.vue';
import VehicleChangeFrequencyStepComponent from '../steps/VehicleChangeFrequencyStepComponent.vue';
import PersonalDetailsStepComponent from '../steps/PersonalDetailsStepComponent.vue';
import ThanksStepComponent from '../steps/ThanksStepComponent.vue';
import ThanksStepComponentB from '../steps/ThanksStepComponentB.vue';

// Test Drive Booking
import TestDriveBookingComponent from '../test-drive/TestDriveBookingComponent.vue';
import TestDriveBookingInterestComponent from '../test-drive/TestDriveBookingInterestComponent.vue';
import TestDriveBookingConfirmationComponent from '../test-drive/TestDriveBookingConfirmationComponent.vue';
import TestDriveRetailerSelectionComponent from '../test-drive/TestDriveRetailerSelectionComponent.vue';

// UI
import UrgencyTickerComponent from '../ui/UrgencyTickerComponent.vue';
import DividerComponent from '../ui/DividerComponent.vue';
import ThanksTickComponent from '../ui/ThanksTickComponent.vue';
import ToggleSwitchComponent from '../ui/ToggleSwitchComponent.vue';
import BrochureDisclaimerComponent from '../ui/BrochureDisclaimerComponent.vue';

// Vehicle
import VehicleCardComponent from '../vehicle/VehicleCardComponent.vue';
import VehicleConfirmationComponent from '../vehicle/VehicleConfirmationComponent.vue';
import VehicleCustomisationComponent from '../vehicle/VehicleCustomisationComponent.vue';
import VehicleLevelFilterComponent from '../vehicle/VehicleLevelFilterComponent.vue';
import VehicleModelFilterComponent from '../vehicle/VehicleModelFilterComponent.vue';
import VehicleTrimComponent from '../vehicle/VehicleTrimComponent.vue';
import RegistrationLookupComponent from '../vehicle/lookup/RegistrationLookupComponent.vue';
import NewUsedVehicleComponent from '../vehicle/NewUsedVehicleComponent.vue';
import ConsiderVehicleStockComponent from '../vehicle/ConsiderVehicleStockComponent.vue';
import AdditionalVehicleInterestComponent from '../vehicle/AdditionalVehicleInterestComponent.vue';
import PartExchangeVehicleComponent from '../vehicle/PartExchangeVehicleComponent.vue';
import DQVehicleCustomisationComponent from '../vehicle/DQVehicleCustomisationComponent.vue';
import DQVehicleCustomisationComponentB from '../vehicle/DQVehicleCustomisationComponentB.vue';
import CurrentVehicleValuationComponent from '../vehicle/CurrentVehicleValuationComponent.vue';
import NotesComponent from '../enquiry/NotesComponent.vue';
import VehicleImageComponent from '../vehicle/VehicleImageComponent.vue';
import MakeModelBotComponent from '../vehicle/make-model-bot/MakeModelBotComponent.vue';
import ChatBotAnswerSupportComponent from '../vehicle/make-model-bot/ChatBotAnswerSupportComponent.vue';

// Vehicle - Cross Sale
import PresubXsaleComponent from '../vehicle/cross-sale/PresubXsaleComponent.vue';
import PresubXsaleFooterComponent from '../vehicle/cross-sale/PresubXsaleFooterComponent.vue';
import PresubXsaleVehiclesComponent from '../vehicle/cross-sale/PresubXsaleVehiclesComponent.vue';

// Vehicle - Trim Bot
import VehicleTrimBotComponent from '../vehicle/VehicleTrimBotComponent.vue';

const EnquiryModule = namespace('enquiryModule');

const dict = {
  en: {
    custom: {
      EmailAddress: {
        required: 'Please enter your email'
      },
      FirstName: {
        required: 'Please enter your first name'
      },
      Surname: {
        required: 'Please enter your last name'
      },
      Telephone: {
        required: 'Please enter your phone number'
      },
      BuildingNumber: {
        required: 'Please enter your building number'
      },
      BuildingName: {
        required: 'Please enter your building name'
      },
      Street: {
        required: 'Please enter your street name'
      },
      Town: {
        required: 'Please enter your town/city'
      },
      Postcode: {
        required: 'Please enter your postcode'
      },
      PostcodeSearch: {
        required: 'Please enter your postcode'
      }
    }
  }
};

Vue.use(VeeValidate, dict);

@Component({
  components: {
  PersonalDetailsComponent,
  VehicleLevelFilterComponent,
  VehicleCustomisationComponent,
  VehicleTrimComponent,
  EnquiryChoicesComponent,
  DerivativePricingComponent,
  VehicleConfirmationComponent,
  AccordionComponent,
  CarouselComponent,
  PurchaseTimeComponent,
  PurchaseTypeComponent,
  TestimonialsComponent,
  ToggleSwitchComponent,
  AddressLookupComponent,
  ProgressBarComponent,
  StartAgainComponent,
  VehicleChangeFrequencyComponent,
  PricingDataComponent,
  PreviousManufacturerDetailsComponent,
  BlurredPricingCurveComponent,
  BlurredPricingCurveBenefitsComponent,
  NextButtonComponent,
  PresubXsaleVehiclesComponent,
  StepHeaderComponent,
  StepHeaderListComponent,
  ThanksTickComponent,
  CompanyDetailsComponent,
  GenericPricingComponent,
  SubmitButtonComponent,
  VehicleSelectionStepComponent,
  ModelOptionsStepComponent,
  AddressStepComponent,
  VehicleChangeFrequencyStepComponent,
  PersonalDetailsStepComponent,
  ThanksStepComponent,
  ThanksStepComponentB,
  PresubXsaleComponent,
  PresubXsaleFooterComponent,
  BlockBodyComponent,
  PostEnquirySettingsComponent,
  PresubMarketingConsentComponent,
  PresubMultiConsentComponent,
  StellantisConsentComponent,
  VehicleCardComponent,
  USPComponent,
  UrgencyTickerComponent,
  PriceHintComponent,
  PublisherContentComponent,
  YouTubeComponent,
  KMIPersonalDetailsComponent,
  CountdownComponent,
  HighlightsComponent,
  RecommendedArticlesComponent,
  CollageComponent,
  ScrollToComponent,
  DividerComponent,
  MandatoryConsentComponent,
  CustomerSatisfactionComponent,
  NPSComponent,
  RegistrationLookupComponent,
  NewUsedVehicleComponent,
  ConsiderVehicleStockComponent,
  PurchaseTypeGroupComponent,
  ContactPreferenceComponent,
  CustomerTypeComponent,
  FundingTypeComponent,
  AdditionalVehicleInterestComponent,
  LeaseInformationComponent,
  VisitedDealershipComponent,
  DigitalBrochureInterestComponent,
  FinanceInformationComponent,
  PartExchangeVehicleComponent,
  DQVehicleCustomisationComponent,
  DQVehicleCustomisationComponentB,
  BusinessInformationComponent,
  BrochureDisclaimerComponent,
  ReviewComponent,
  VerificationCodeComponent,
  CallMeNowComponent,
  MotorwayComponent,
  CurrentVehicleValuationComponent,
  MotorwayValuationWrapperComponent,
  KMIInterestComponent,
  AudienceSegmentationComponent,
  TestDriveBookingComponent,
  TestDriveBookingConfirmationComponent,
  TestDriveBookingInterestComponent,
  TestDriveRetailerSelectionComponent,
  PhoneNumberComponent,
  EnquiryServicesComponent,
  NotesComponent,
  CurrentRecentManufacturerDetailsComponent,
  NissanPresubMultiConsentComponent,
  RetailerSelectionComponent,
  RetailerConfirmationComponent,
  VehicleImageComponent,
  VehicleModelFilterComponent,
  GoToStepButtonComponent,
  ExtraDataComponent,
  VehicleTrimBotComponent,
  ExitIntentSurveyComponent,
  MakeModelBotComponent,
  EmailCaptureComponent,
  ChatBotAnswerSupportComponent,
  MercedesPostEnquiryConsentComponent,
  HelpBannerComponent
  }
})
export default class FormStepComponent extends Vue {
  @EnquiryModule.Getter public currentStep;

  @EnquiryModule.Getter public interactedSteps;

  @Provide() validate: Validator = this.$validator;

  @Prop({ default: null })
  public settings: any;

  @Prop({ default: null })
  public formStepId!: number;

  public get validationScope(): string {
    return `step${this.formStepId}Validator`;
  }

  public blockContainer(isBlockContainer, isBlockCompact) {
    let cssString = '';

    if (isBlockContainer) {
      cssString += 'block__container';
    }

    if (isBlockCompact) {
      cssString += ' block__container--compact';
    }

    return cssString;
  }

  public backgroundColour(backgroundColour) {
    return backgroundColour !== null && backgroundColour !== ''
      ? `block--${backgroundColour}`
      : '';
  }

  public appendUrl() {
    if (this.currentStep === 1) {
      if (window.location.hash) {
        // eslint-disable-next-line no-restricted-globals
        history.replaceState(
          {},
          document.title,
          window.location.href.split('#')[0]
        );
      }
    } else {
      window.location.hash = `#step${this.currentStep}`;
    }
  }

  @Watch('currentStep')
  public scrollTo() {
    this.appendUrl();

    return scrollService.scrollToTop();
  }

  public created() {
    this.appendUrl();
    this.$validator.validateAll(this.validationScope);
  }
}
</script>
